import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import type { Observable } from 'rxjs';
import { Subscription, fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonColor, ButtonSize } from '../button';
import {
  FileSelectAlignment,
  FileSelectMode,
  FlDragEvent,
} from './file-select.types';
import { ButtonComponent } from '../button/button.component';

@Component({
  standalone: true,
  selector: 'fl-file-select',
  imports: [ButtonComponent, IconModule, ValidationErrorComponent],
  template: `
    @if (contentProvided) {
      @if (
        fileSelectMode === FileSelectMode.DRAGDROP &&
        !hasCustomFileHover &&
        fileHover
      ) {
        <div
          class="FileHoverOverlay"
          (dragleave)="onDragLeave()"
        >
          <fl-icon
            class="FileHoverChild"
            i18n-label="File select drop file"
            label="Drop your file to send as attachment"
            [color]="IconColor.LIGHT"
            [useIconFont]="true"
            [size]="IconSize.MID"
            [name]="'ui-backup'"
          ></fl-icon>
          <span
            class="FileHoverText FileHoverChild"
            i18n="File select drop to attach file helper text"
          >
            Drop your files here
          </span>
        </div>
      }
      @if (!disableDragDrop) {
        <input
          #fileInput
          class="HiddenInput"
          title="Drop to attach your file"
          i18n-title="File input tooltip"
          type="file"
          [attr.accept]="fileTypes"
          [multiple]="multiple"
          [disabled]="control.disabled || null"
          (change)="onFileSelect(fileInput.files)"
        />
      } @else {
        <input
          #fileInput
          class="HiddenInput"
          type="file"
          [attr.accept]="fileTypes"
          [multiple]="multiple"
          [disabled]="control.disabled || null"
          (change)="onFileSelect(fileInput.files)"
        />
      }
    }

    <div
      [class.Content]="contentProvided"
      #content
    >
      <ng-content></ng-content>
    </div>

    <!-- Default content - displayed if there is no ng-content -->
    @if (!contentProvided) {
      @if (fileSelectMode === FileSelectMode.DRAGDROP) {
        <div
          class="DefaultContent"
          [class.FileHover]="fileHover"
          [class.IsInvalid]="isInvalid === true"
          [attr.data-alignment]="alignment"
          [attr.data-vertical-edge-to-edge]="verticalEdgeToEdge"
        >
          <div
            class="DefaultContent-wrapper"
            [class.ShowButton]="showButton"
          >
            <div
              class="DefaultContent-inner {{
                showButton && alignment === 'center' ? 'mb-xsmall' : 'mb-none'
              }}"
            >
              <fl-icon
                class="DefaultContent-icon mr-small"
                i18n-label="File select icon label"
                label="Select file to send as attachment"
                [name]="'ui-backup'"
                [useIconFont]="true"
                [size]="IconSize.MID"
              ></fl-icon>
              <div class="DefaultContent-text">
                <p
                  class="FileNameTruncate text-body-16 font-medium"
                  i18n="File select helper text"
                >
                  @if (fileHover) {
                    <span> Drop your files here </span>
                  }
                  @if (!fileHover) {
                    <span>
                      <span class="mobile:show">Attach files</span>
                      <span class="mobile:hide">
                        @if (!fileUploaded || multiple) {
                          <span>
                            Drag and drop or
                            <span class="DefaultContent-highlight"
                              >click to upload</span
                            >
                          </span>
                        }
                      </span>
                    </span>
                  }

                  @if (fileUploaded && !multiple && control.value) {
                    <span>
                      {{ control.value[0].name }}
                    </span>
                  }
                </p>
                <div class="FormatAndNote">
                  @if (formatNote) {
                    <p
                      class="text-body-16 mr-xxsmall"
                      i18n="format allowed"
                    >
                      Accepted format: {{ formatNote }}
                    </p>
                  }
                  @if (note) {
                    <p class="text-body-16">
                      {{ note }}
                    </p>
                  }
                </div>
              </div>
            </div>
            @if (showButton) {
              <fl-button
                i18n="choose file"
                class="Btn"
                [size]="ButtonSize.MINI"
                [color]="ButtonColor.DEFAULT"
                (click)="triggerInput()"
              >
                Upload File
              </fl-button>
            }
          </div>
        </div>
      }
      @if (fileSelectMode === FileSelectMode.ICON) {
        <fl-button [disabled]="control.disabled">
          <fl-icon
            i18n-label="Attach file icon label"
            label="Attach a file"
            [color]="control.disabled ? IconColor.FOREGROUND : iconColor"
            [size]="IconSize.SMALL"
            [name]="'ui-attachment'"
            [useIconFont]="true"
          ></fl-icon>
        </fl-button>
      }
      <input
        #fileInput
        class="FileInput"
        title="Click or drop to attach your file"
        i18n-title="File input tooltip"
        type="file"
        [attr.accept]="fileTypes"
        [multiple]="multiple"
        [disabled]="control.disabled || null"
        (change)="onFileSelect(fileInput.files)"
        (click)="handleClick()"
        (dragleave)="onDragLeave()"
        (mouseenter)="onMouseOver(true)"
        (mouseleave)="onMouseOver(false)"
      />
    }
    @if (displayValidation) {
      <fl-validation-error
        [control]="control"
        [showIcon]="false"
      ></fl-validation-error>
    }
  `,
  styleUrls: ['./file-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileSelectComponent implements OnChanges, OnInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FileSelectMode = FileSelectMode;
  IconColor = IconColor;
  IconSize = IconSize;

  fileHover = false;
  contentProvided = false;
  iconColor = IconColor.FOREGROUND;
  @RepetitiveSubscription()
  private errorSubscription?: Subscription;

  @Input() fileSelectMode?: FileSelectMode;
  @Input() fileTypes?: string;
  @Input() control = new FormControl<readonly File[]>([], {
    nonNullable: true,
  });
  @Input() note: string;
  @Input() formatNote: string;
  @Input() showButton = false;
  @Input() alignment: FileSelectAlignment = 'center';
  @Input() verticalEdgeToEdge = false;
  @Input() disableDragDrop = false;
  @Input() displayValidation = true;
  @Input() hasCustomFileHover = false;

  /** Native multiple attribute: allows multiple files at once */
  @Input() multiple = false;

  @Output() onFileHover = new EventEmitter<boolean>();

  @ViewChild('fileInput')
  nativeElement: ElementRef<HTMLInputElement>;

  @ViewChild('content', { read: ElementRef, static: true })
  content: ElementRef<HTMLDivElement>;

  @HostBinding('class.AsIcon') asIcon: boolean;

  isInvalid: boolean;
  fileUploaded = false;

  ngOnInit(): void {
    this.asIcon = this.fileSelectMode === FileSelectMode.ICON;
    this.contentProvided = !!(this.content.nativeElement.children.length > 0);

    if (
      this.disableDragDrop &&
      (!this.contentProvided || this.fileSelectMode === FileSelectMode.DRAGDROP)
    ) {
      throw new Error('Cannot disable drag and drop');
    }

    if (!this.disableDragDrop && this.fileSelectMode === undefined) {
      this.fileSelectMode = FileSelectMode.DRAGDROP;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      if (this.errorSubscription) {
        this.errorSubscription.unsubscribe();
      }
      this.errorSubscription = this.control.statusChanges.subscribe(() => {
        if (this.control.value?.length > 0) {
          this.isInvalid = this.control.invalid;
          this.fileUploaded = true;
        } else {
          this.isInvalid = false;
          this.fileUploaded = false;
        }

        this.cd.markForCheck();
      });
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: FlDragEvent): void {
    if (this.control.disabled || this.disableDragDrop) {
      return;
    }
    if (this.hasCustomFileHover) {
      this.onFileHover.emit(true);
    } else {
      this.fileHover = true;
    }
    event.preventDefault();
  }

  onDragLeave(): void {
    if (this.control.disabled || this.disableDragDrop) {
      return;
    }
    if (this.hasCustomFileHover) {
      this.onFileHover.emit(false);
    } else {
      this.fileHover = false;
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: FlDragEvent): void {
    if (this.control.disabled || this.disableDragDrop) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      this.onFileSelect(event.dataTransfer.files);
    }
    if (this.hasCustomFileHover) {
      this.onFileHover.emit(false);
    } else {
      this.fileHover = false;
    }
  }

  onMouseOver(mouseOver: boolean): void {
    if (mouseOver) {
      this.iconColor = IconColor.SECONDARY;
    } else {
      this.iconColor = IconColor.FOREGROUND;
    }
  }

  onFileSelect(fileList: FileList | null): void {
    if (fileList) {
      const files = Array.from(fileList);
      this.control.setValue(files);
      this.control.markAsDirty();
    }

    this.resetInputValue();
  }

  ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
  }

  /**
   * Trigger the file select dialog
   * Can be called from outside this component.
   */
  triggerInput(): void {
    this.nativeElement.nativeElement.click();
  }

  handleClick(): void {
    this.resetInputValue();
  }

  resetInputValue(): void {
    // This re-sets the selected filename of the file input element to blank
    // To allow the change event to be triggered and detected in the file input
    // So that files with the same filename can successfully be re-selected
    this.nativeElement.nativeElement.value = '';
  }

  /**
   * This function is going to be deprecated, since it is not UI testable
   * Please consider use ReadFileService.getBase64StringFromFile
   *
   * @param file
   */
  readFileAsDataURL(file: File): Observable<string | null> {
    const fileReader = new FileReader();

    fileReader.readAsDataURL(file);

    // We can cast to string here as we are using the `readAsDataURL` function,
    // the result will always be a string.
    return fromEvent(fileReader, 'load').pipe(
      map(() => fileReader.result as string),
    );
  }
}
